import { useMemo } from 'react';

const MAX_RESULTS_CAP = 500;
const DEFAULT_MAX_RESULTS = 20;

const CURRENT_SPACE_KEY_TOKEN = 'currentSpace()';

export enum SortOption {
	Modified = 'lastModified',
	Created = 'created',
	Title = 'title',
}

// BE sends long-form enum values; normalise to CQL field names
const SORT_PARAM_TO_CQL: Record<string, SortOption> = {
	'Page modified': SortOption.Modified,
	modified: SortOption.Modified,
	'Page created': SortOption.Created,
	created: SortOption.Created,
	'Page title': SortOption.Title,
	title: SortOption.Title,
};

export interface ContentReportTableMacroParams {
	labels?: { value?: string };
	spaces?: { value?: string };
	sort?: { value?: string };
	reverse?: { value?: string };
	maxResults?: { value?: string };
	showCommentsCount?: { value?: string };
	showLikesCount?: { value?: string };
	blankTitle?: { value?: string };
	blankDescription?: { value?: string };
	contentBlueprintId?: { value?: string };
	blueprintModuleCompleteKey?: { value?: string };
	createButtonLabel?: { value?: string };
	// passed by extension handler to resolve currentSpace()
	currentSpaceKey?: string;
}

export interface ContentReportTableMacroParamsForQuery {
	cql: string;
	first: number;
	includeComments: boolean;
	includeLikes: boolean;
}

export interface ParsedContentReportTableParams {
	labels: string[];
	spaces: string[];
	cql: string;
	maxResults: number;
	showCommentsCount: boolean;
	showLikesCount: boolean;
	blankTitle: string;
	blankDescription: string;
	contentBlueprintId: string;
	blueprintModuleCompleteKey: string;
	createButtonLabel: string;
	labelsError: boolean;
	macroParamsForQuery: ContentReportTableMacroParamsForQuery;
}

function splitTrimToArray(str: string | undefined): string[] {
	if (!str || str.trim() === '') {
		return [];
	}
	return str.split(/\s*,\s*/).filter((s) => s.length > 0);
}

function parseBoolean(value: string | undefined, defaultValue: boolean): boolean {
	if (value === undefined || value === '') {
		return defaultValue;
	}
	return value === 'true';
}

function parseMaxResults(value: string | undefined): number {
	if (value === undefined || value === '') {
		return DEFAULT_MAX_RESULTS;
	}
	const parsed = parseInt(value, 10);
	if (isNaN(parsed) || parsed <= 0) {
		return DEFAULT_MAX_RESULTS;
	}
	return Math.min(parsed, MAX_RESULTS_CAP);
}

function parseSortOption(value: string | undefined): SortOption {
	if (!value) {
		return SortOption.Modified;
	}
	return SORT_PARAM_TO_CQL[value] ?? SortOption.Modified;
}

function buildCql(labels: string[], spaces: string[], sort: SortOption, reverse: boolean): string {
	if (labels.length === 0) {
		return '';
	}

	const labelClause = `label in (${labels.map((l) => `"${l}"`).join(',')})`;
	const typeClause = `type in (page,blogpost)`;

	const parts = [labelClause, typeClause];

	if (spaces.length > 0) {
		const spaceClause = `space in (${spaces.map((s) => `"${s}"`).join(',')})`;
		parts.push(spaceClause);
	}

	const direction = reverse ? 'desc' : 'asc';
	const orderClause = `ORDER BY ${sort} ${direction}`;

	return `${parts.join(' AND ')} ${orderClause}`;
}

/**
 * Pure function for building GraphQL query variables from raw macro params.
 * Can be used at render time and during SSR query preloading.
 */
export function composeMacroParamsForQuery(
	macroParams: Record<string, { value?: string } | undefined> | undefined,
	currentSpaceKey?: string,
): ContentReportTableMacroParamsForQuery {
	const rawLabels = macroParams?.labels?.value;
	const rawSpaces = macroParams?.spaces?.value;

	const labels = splitTrimToArray(rawLabels);
	const spaces = splitTrimToArray(rawSpaces).map((s) =>
		s === CURRENT_SPACE_KEY_TOKEN ? (currentSpaceKey ?? s) : s,
	);

	const sort = parseSortOption(macroParams?.sort?.value);
	const reverse = parseBoolean(macroParams?.reverse?.value, true);
	const maxResults = parseMaxResults(macroParams?.maxResults?.value);
	const showCommentsCount = parseBoolean(macroParams?.showCommentsCount?.value, false);
	const showLikesCount = parseBoolean(macroParams?.showLikesCount?.value, false);

	const cql = buildCql(labels, spaces, sort, reverse);

	return {
		cql,
		first: maxResults,
		includeComments: showCommentsCount,
		includeLikes: showLikesCount,
	};
}

export function useParsedMacroParams(
	macroParams: ContentReportTableMacroParams | undefined,
): ParsedContentReportTableParams {
	return useMemo(() => {
		const rawLabels = macroParams?.labels?.value;
		const rawSpaces = macroParams?.spaces?.value;
		const currentSpaceKey = macroParams?.currentSpaceKey;

		const labels = splitTrimToArray(rawLabels);
		const spaces = splitTrimToArray(rawSpaces).map((s) =>
			s === CURRENT_SPACE_KEY_TOKEN ? (currentSpaceKey ?? s) : s,
		);

		const labelsError = labels.length === 0;

		const sort = parseSortOption(macroParams?.sort?.value);
		const reverse = parseBoolean(macroParams?.reverse?.value, true);
		const maxResults = parseMaxResults(macroParams?.maxResults?.value);
		const showCommentsCount = parseBoolean(macroParams?.showCommentsCount?.value, false);
		const showLikesCount = parseBoolean(macroParams?.showLikesCount?.value, false);

		const blankTitle = macroParams?.blankTitle?.value ?? '';
		const blankDescription = macroParams?.blankDescription?.value ?? '';
		const contentBlueprintId = macroParams?.contentBlueprintId?.value ?? '';
		const blueprintModuleCompleteKey = macroParams?.blueprintModuleCompleteKey?.value ?? '';
		const createButtonLabel = macroParams?.createButtonLabel?.value ?? '';

		const cql = buildCql(labels, spaces, sort, reverse);

		const macroParamsForQuery: ContentReportTableMacroParamsForQuery = {
			cql,
			first: maxResults,
			includeComments: showCommentsCount,
			includeLikes: showLikesCount,
		};

		return {
			labels,
			spaces,
			cql,
			maxResults,
			showCommentsCount,
			showLikesCount,
			blankTitle,
			blankDescription,
			contentBlueprintId,
			blueprintModuleCompleteKey,
			createButtonLabel,
			labelsError,
			macroParamsForQuery,
		};
	}, [macroParams]);
}
